import React, { useEffect, useState, useMemo } from "react";

import {
  OnePagePortfolioAssessmentAnswer,
  OnePagePortfolioAssessment,
} from "../../../state";
import Modal from "../../../atoms/Modal";
import Button from "../../../atoms/ActiveLearning/Button";
import BAWorkGuideModal from "../../teacher/BAWorkGuideModal";
import BAWorkAnswersModal from "../../../templates/teacher/BAWorkAnswersModal";
import { ReactComponent as QuestionMark } from "../../../assets/svg/before_after_work_guide_question.svg";
import img from "../../../assets/images/oppa-before-modal_img.webp";
import { ReactComponent as CloseIcon } from "../../../assets/svg/oppa-before-modal_close.svg";

import styles from "./BeforeModal.module.scss";

export type BeforeModalProps = {
  isTeacher?: boolean;
  classList?: number[];
  assessmentId: number;
  assessmentData: OnePagePortfolioAssessment | null;
  calling?: boolean;
  isRegisterOPPAAnswersSuccess?: boolean;
  canViewEveryoneAnswers?: boolean;
  fetchOnePagePortfolioAssessment?: (id: number) => void;
  registerOnePagePortfolioAssessmentAnswers?: (
    id: number,
    answers: OnePagePortfolioAssessmentAnswer[],
  ) => void;
  onNext: () => void;
  onClose: () => void;
};

const BeforeModal: React.FC<BeforeModalProps> = ({
  isTeacher,
  classList,
  assessmentId,
  assessmentData,
  calling,
  isRegisterOPPAAnswersSuccess,
  canViewEveryoneAnswers,
  fetchOnePagePortfolioAssessment,
  registerOnePagePortfolioAssessmentAnswers,
  onNext,
  onClose,
}) => {
  const [answer, setAnswer] = useState<string>("");
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [pendingNext, setPendingNext] = useState(false);

  useEffect(() => {
    if (!fetchOnePagePortfolioAssessment) return;

    fetchOnePagePortfolioAssessment(assessmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const assessmentTitle = useMemo(() => {
    const title = assessmentData?.title ?? "";
    return title.replace(/\s*-高校-|\s*-中学-/g, "");
  }, [assessmentData]);

  const question = useMemo(() => {
    const issue = assessmentData?.one_page_portfolio_assessment_issues?.[0];
    const question = issue?.one_page_portfolio_assessment_questions?.[0];

    return {
      id: question?.id ?? 0,
      title: question?.title ?? "",
      answer: question?.one_page_portfolio_assessment_answer?.contents ?? "",
    };
  }, [assessmentData]);

  useEffect(() => {
    setAnswer(question.answer);
  }, [question.answer]);

  const checkAnswer = useMemo(() => {
    const hasChanges = answer !== question.answer;
    const isEmpty = !answer.trim();

    return hasChanges && !isEmpty;
  }, [answer, question.answer]);

  const handleSubmit = () => {
    if (!registerOnePagePortfolioAssessmentAnswers) return;

    const assessmentAnswer: OnePagePortfolioAssessmentAnswer[] = [
      {
        one_page_portfolio_assessment_question_id: question.id,
        contents: answer,
      },
    ];
    registerOnePagePortfolioAssessmentAnswers(assessmentId, assessmentAnswer);
  };

  const handleClose = () => {
    setPendingNext(false);
    if (!isTeacher && checkAnswer) handleSubmit();
    onClose();
  };

  useEffect(() => {
    if (!pendingNext || !isRegisterOPPAAnswersSuccess) return;
    onClose();
    onNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegisterOPPAAnswersSuccess, pendingNext]);

  const handleNext = () => {
    if (!isTeacher && checkAnswer) {
      setPendingNext(true);
      handleSubmit();
    } else {
      onClose();
      onNext();
    }
  };

  return (
    <>
      {showGuideModal && (
        <BAWorkGuideModal onCloseModal={() => setShowGuideModal(false)} />
      )}
      {showAnswersModal && (
        <BAWorkAnswersModal
          oppaId={assessmentId}
          classList={classList ?? []}
          onCloseModal={() => setShowAnswersModal(false)}
        />
      )}
      <Modal onClose={handleClose} className={styles.beforeModal}>
        <CloseIcon className={styles.closeIcon} onClick={handleClose} />
        <div className={styles.wrapper}>
          <img className={styles.img} src={img} alt="" aria-hidden="true" />
          <div className={styles.questionBlock}>
            <div className={styles.titleWrapper}>
              <p className={styles.title}>{assessmentTitle}</p>
              {isTeacher && (
                <div
                  className={styles.questionMarkWrapper}
                  onClick={() => setShowGuideModal(true)}
                >
                  <QuestionMark />
                </div>
              )}
            </div>
            <p className={styles.questionTitle}>{question.title}</p>
            <p className={styles.annotation}>※回答は先生にのみ送信されます。</p>
          </div>
          <textarea
            className={styles.textarea}
            value={answer}
            placeholder={
              isTeacher
                ? "入力は生徒画面で行われます。先生画面では閲覧のみ可能です。"
                : "テキストを入力してください。"
            }
            onChange={e => setAnswer(e.target.value)}
            disabled={isTeacher}
            maxLength={2000}
          />

          <div className={styles.buttonArea}>
            {isTeacher ? (
              <Button
                disabled={!canViewEveryoneAnswers}
                handleClick={() => {
                  setShowAnswersModal(true);
                }}
              >
                みんなの回答を見る
              </Button>
            ) : (
              <Button
                handleClick={() => {
                  setPendingNext(false);
                  handleSubmit();
                }}
                disabled={calling}
              >
                回答を送信する
              </Button>
            )}
            <Button
              handleClick={handleNext}
              variant="secondaryBgWhite"
              disabled={calling}
            >
              次のページへ進む
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BeforeModal;
